import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Chip,
  Checkbox,
  Divider,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import {
  getAllUsers,
  updateUser,
  createUser,
  deleteUser,
} from 'common/src/api/users';
import { sendEmail } from 'common/src/api/email';
import { User as BaseUser } from 'common/src/utils/types';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { generateWelcomeEmail } from '../../components/emails/WelcomeEmail';
import { useSectionItems } from '../../components/layouts/sidebars/SidebarItems';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import Header from '../../components/layouts/Header';
import { LoadingState, PageLayout } from '../../components/ui';
import {
  buildAvailablePermissions,
  type UserPermission,
} from '../../utils/user-permissions';


// Extendemos el tipo User para incluir permisos
interface User extends BaseUser {
  permissions?: UserPermission[];
}

const TeamPage = () => {
  const { t } = useTranslation('team');
  const { userProfile, session } = useUserStore();
  const sidebarItems = useSectionItems(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<
    Partial<User> & {
      password?: string;
      permissions?: UserPermission[];
    }
  >({
    email: '',
    first_name: '',
    last_name: '',
    roles: ['basic'],
    password: '',
    permissions: [],
  });
  const [loadingAction, setLoadingAction] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState<
    UserPermission[]
  >([]);

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  // Genera la lista de permisos disponibles a partir de los elementos del sidebar
  const generateAvailablePermissions = useCallback(() => {
    const permissions = buildAvailablePermissions(sidebarItems, {
      hasVolume: userProfile?.exporter?.industry_volume_access === 'full',
    });

    setAvailablePermissions(permissions);

    // Inicializar los permisos del nuevo usuario
    setNewUser((prev) => ({
      ...prev,
      permissions: permissions,
    }));
  }, [sidebarItems, userProfile?.exporter?.industry_volume_access]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedUsers = (await getAllUsers()) as User[];

      // Filter users by the current user's exporter_id if they're an admin
      let filteredUsers = fetchedUsers;

      if (
        userProfile?.exporter_id &&
        !userProfile?.roles.includes('superadmin')
      ) {
        filteredUsers = fetchedUsers.filter(
          (user) => user.exporter_id === userProfile.exporter_id
        );
      }

      // Filtrar usuarios internos (nosotros): todo @cherrymarket.cl, cualquier
      // cuenta de nicopirozzi y las nuestras metidas en equipos de exportadoras.
      const finalFilteredUsers = filteredUsers.filter((user) => {
        const email = user.email.toLowerCase().trim();
        return (
          !email.endsWith('@cherrymarket.cl') &&
          !email.includes('nicopirozzi') &&
          ![
            'vicente.pirozzi@conectikids.cl',
            'juanpablogrez@gmail.com',
            'jpgrez@xsur.cl',
            'demo@frutoslaaguada.cl',
          ].includes(email)
        );
      });

      setUsers(finalFilteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('Error al cargar usuarios'));
    } finally {
      setLoading(false);
    }
  }, [t, userProfile?.exporter_id, userProfile?.roles]);

  useEffect(() => {
    fetchUsers();
    generateAvailablePermissions();
  }, [fetchUsers, generateAvailablePermissions]);

  const handleCreateUser = async () => {
    if (
      !newUser.email ||
      !newUser.password ||
      !newUser.first_name ||
      !newUser.last_name
    ) {
      toast.error(t('Por favor complete todos los campos requeridos'));
      return;
    }

    setLoadingAction(true);

    try {
      // Add the current user's exporter_id to the new user
      const userData = {
        ...newUser,
        exporter_id: userProfile?.exporter_id,
        exporter: userProfile?.exporter,
      };

      // Si es usuario basic, enviamos los permisos seleccionados
      if (newUser.roles && newUser.roles[0] === 'basic') {
        userData.permissions = newUser.permissions?.filter((p) => p.enabled);
      } else {
        // Si no es basic, no enviamos permisos
        userData.permissions = [];
      }

      await createUser(userData, session);

      // Send welcome email with credentials
      if (userProfile?.exporter_id) {
        await sendEmail({
          exporter_id: userProfile.exporter_id,
          subject: 'Bienvenido a CherryMarket / FruitMarkets',
          html: generateWelcomeEmail({
            firstName: newUser.first_name || '',
            email: newUser.email || '',
            password: newUser.password || '',
          }),
          to: [newUser.email || ''],
        });
      }

      toast.success(
        t(
          'Usuario creado con éxito y correo de bienvenida con contraseña enviado'
        )
      );
      onNewClose();

      // Reset form and refresh user list
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        roles: ['basic'],
        password: '',
        permissions: availablePermissions.map((p) => ({
          ...p,
          enabled: false,
        })),
      });

      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const message = error instanceof Error ? error.message : '';
      if (message === 'Not authorized to create users for this exporter') {
        // Cuenta compartida (p. ej. demo): alguien la cambió de exportadora
        // después de que se cargó esta pantalla.
        toast.error(
          t(
            'Tu usuario ahora está en otra exportadora. Recarga la página y vuelve a intentarlo.'
          )
        );
      } else if (/already been registered/i.test(message)) {
        toast.error(t('Ya existe un usuario con ese email.'));
      } else {
        toast.error(
          message
            ? t('Error al crear usuario: {{error}}', { error: message })
            : t('Error al crear usuario')
        );
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEditUser = (user: User) => {
    // Preparar los permisos para edición
    let userPermissions = [...availablePermissions];

    // Si el usuario tiene permisos, actualizamos el estado de enabled
    if (user.permissions) {
      userPermissions = userPermissions.map((perm) => {
        const existingPerm = user.permissions?.find(
          (p) => p.sidebarItemKey === perm.sidebarItemKey
        );
        return {
          ...perm,
          enabled: existingPerm ? existingPerm.enabled : false,
        };
      });
    }

    setSelectedUser({
      ...user,
      permissions: userPermissions,
    });
    onEditOpen();
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setLoadingAction(true);
    try {
      // Solo enviar los campos que realmente pueden ser actualizados
      const userData: Partial<User> = {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,

        roles: selectedUser.roles,
      };

      // Si es usuario basic, enviamos solo los permisos habilitados
      if (selectedUser.roles && selectedUser.roles[0] === 'basic') {
        userData.permissions = selectedUser.permissions?.filter(
          (p) => p.enabled
        );
      } else {
        // Si no es basic, no enviamos permisos
        userData.permissions = [];
      }

      await updateUser(selectedUser.id, userData, session);
      toast.success(t('Usuario actualizado exitosamente'));
      onEditClose();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('Error al actualizar usuario'));
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setLoadingAction(true);
    try {
      await deleteUser(selectedUser.id, session);
      toast.success(t('Usuario eliminado exitosamente'));
      onDeleteClose();
      fetchUsers();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      toast.error(t('Error al eliminar usuario'));
    } finally {
      setLoadingAction(false);
    }
  };

  const confirmDelete = (user: User) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const togglePermission = (index: number, isNew: boolean = false) => {
    if (isNew) {
      // Para nuevo usuario
      const updatedPermissions = [...(newUser.permissions || [])];
      updatedPermissions[index].enabled = !updatedPermissions[index].enabled;
      setNewUser({
        ...newUser,
        permissions: updatedPermissions,
      });
    } else {
      // Para usuario existente
      if (selectedUser && selectedUser.permissions) {
        const updatedPermissions = [...selectedUser.permissions];
        updatedPermissions[index].enabled = !updatedPermissions[index].enabled;
        setSelectedUser({
          ...selectedUser,
          permissions: updatedPermissions,
        });
      }
    }
  };

  const handleRoleChange = (value: string, isNew: boolean = false) => {
    if (isNew) {
      setNewUser({
        ...newUser,
        roles: [value],
      });
    } else if (selectedUser) {
      setSelectedUser({
        ...selectedUser,
        roles: [value],
      });
    }
  };

  const generateRandomPassword = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({ ...newUser, password });
  };

  if (loading) {
    return <LoadingState label={t('Cargando usuarios...')} />;
  }

  const buildAccessSummary = (permissions: UserPermission[]) => {
    const readSet = new Set<string>();
    const writeSet = new Set<string>();

    permissions
      .filter((p) => p.enabled)
      .forEach((p) => {
        p.readAccess?.forEach((label) => readSet.add(label));
        p.writeAccess?.forEach((label) => writeSet.add(label));
      });

    return { read: Array.from(readSet), write: Array.from(writeSet) };
  };

  // Componente para mostrar la sección de permisos
  const PermissionsSection = ({
    permissions,
    isNew = false,
    className = '',
  }: {
    permissions: UserPermission[];
    isNew?: boolean;
    className?: string;
  }) => {
    const { read, write } = buildAccessSummary(permissions);
    const hasAnyAccess = read.length > 0 || write.length > 0;
    const hasAnyEnabled = permissions.some((p) => p.enabled);

    return (
      <div className={`space-y-3 ${className}`}>
        <h3 className='text-medium font-semibold'>{t('Permisos de acceso')}</h3>
        {hasAnyEnabled && hasAnyAccess && (
          <div className='rounded-large border border-default-200 bg-default-50 p-3 space-y-2'>
            <p className='text-tiny font-semibold text-default-500 uppercase tracking-wide'>
              Acceso a datos
            </p>
            <div className='flex flex-col gap-1.5'>
              {read.length > 0 && (
                <div className='flex items-center gap-2'>
                  <Icon
                    icon='tabler:eye'
                    className='text-success shrink-0'
                    width={16}
                  />
                  <span className='text-small text-default-500'>Lectura:</span>
                  <div className='flex flex-wrap gap-1'>
                    {read.map((label) => (
                      <Chip
                        key={label}
                        size='sm'
                        variant='flat'
                        classNames={{ content: 'text-tiny font-medium' }}
                      >
                        {label}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
              {write.length > 0 && (
                <div className='flex items-center gap-2'>
                  <Icon
                    icon='tabler:pencil'
                    className='text-warning shrink-0'
                    width={16}
                  />
                  <span className='text-small text-default-500'>
                    Escritura:
                  </span>
                  <div className='flex flex-wrap gap-1'>
                    {write.map((label) => (
                      <Chip
                        key={label}
                        size='sm'
                        variant='flat'
                        classNames={{ content: 'text-tiny font-medium' }}
                      >
                        {label}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <Divider />
        <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
          {permissions.map((permission, index) => (
            <Checkbox
              key={permission.sidebarItemKey}
              isSelected={permission.enabled}
              onChange={() => togglePermission(index, isNew)}
              size='sm'
            >
              {permission.title}
            </Checkbox>
          ))}
        </div>
      </div>
    );
  };

  return (
    <PageLayout>
      <Header
        title={t('Equipo')}
        description={t('Gestiona los usuarios de tu organización')}
        actions={
          <Button color='primary' startContent={<Icon icon='tabler:plus' />} onClick={onNewOpen}>
            {t('Agregar Usuario')}
          </Button>
        }
      />

      <Table aria-label={t('Tabla de usuarios')}>
        <TableHeader>
          <TableColumn>{t('NOMBRE')}</TableColumn>
          <TableColumn>{t('EMAIL')}</TableColumn>
          <TableColumn>{t('ROL')}</TableColumn>
          <TableColumn>{t('ACCIONES')}</TableColumn>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                {user.roles?.map((role) => (
                  <Chip key={role} variant='flat' className='mr-1' size='sm'>
                    {role}
                  </Chip>
                ))}
              </TableCell>
              <TableCell>
                <div className='flex gap-2'>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    onClick={() => handleEditUser(user)}
                  >
                    <Icon icon='tabler:edit' />
                  </Button>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    color='danger'
                    onClick={() => confirmDelete(user)}
                  >
                    <Icon icon='tabler:trash' />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* New User Modal */}
      <Modal
        isOpen={isNewOpen}
        onClose={onNewClose}
        size='lg'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>{t('Agregar Nuevo Miembro')}</ModalHeader>
          <ModalBody>
            <div className='grid grid-cols-2 gap-4'>
              <Input
                label={t('Nombre')}
                value={newUser.first_name}
                onChange={(e) =>
                  setNewUser({ ...newUser, first_name: e.target.value })
                }
                isRequired
              />
              <Input
                label={t('Apellido')}
                value={newUser.last_name}
                onChange={(e) =>
                  setNewUser({ ...newUser, last_name: e.target.value })
                }
                isRequired
              />
            </div>
            <Input
              label={t('Email')}
              type='email'
              value={newUser.email}
              onChange={(e) =>
                setNewUser({ ...newUser, email: e.target.value })
              }
              isRequired
            />
            <div className='flex gap-2'>
              <Input
                label={t('Contraseña')}
                value={newUser.password || ''}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    password: e.target.value,
                  })
                }
                isRequired
                className='flex-1'
              />
              <Button
                size='sm'
                variant='flat'
                onClick={generateRandomPassword}
                className='mt-6'
              >
                {t('Generar')}
              </Button>
            </div>
            <Select
              label={t('Rol')}
              selectedKeys={newUser.roles}
              onChange={(e) => handleRoleChange(e.target.value, true)}
              isRequired
            >
              <SelectItem key='basic' value='basic'>
                {t('Básico (Permisos personalizados)')}
              </SelectItem>
              <SelectItem key='admin' value='admin'>
                {t('Admin (Acceso completo)')}
              </SelectItem>
            </Select>

            {/* Mostrar permisos solo si el rol seleccionado es 'basic' */}
            {newUser.roles &&
              newUser.roles[0] === 'basic' &&
              newUser.permissions && (
                <PermissionsSection
                  permissions={newUser.permissions}
                  isNew={true}
                  className='mt-4'
                />
              )}

            <p className='text-sm text-default-500 mt-2'>
              *{' '}
              {t(
                'Se enviará un correo de bienvenida con las credenciales al usuario'
              )}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={onNewClose}>
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              onPress={handleCreateUser}
              isLoading={loadingAction}
            >
              {t('Crear Usuario')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={onEditClose}
        size='lg'
        scrollBehavior='inside'
      >
        <ModalContent>
          {selectedUser && (
            <>
              <ModalHeader>{t('Editar Usuario')}</ModalHeader>
              <ModalBody>
                <div className='grid grid-cols-2 gap-4'>
                  <Input
                    label={t('Nombre')}
                    value={selectedUser.first_name}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        first_name: e.target.value,
                      })
                    }
                  />
                  <Input
                    label={t('Apellido')}
                    value={selectedUser.last_name}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        last_name: e.target.value,
                      })
                    }
                  />
                </div>
                <Input
                  label={t('Email')}
                  value={selectedUser.email}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, email: e.target.value })
                  }
                  isDisabled
                />
                <Select
                  label={t('Rol')}
                  selectedKeys={selectedUser.roles}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <SelectItem key='basic' value='basic'>
                    {t('Básico (Permisos personalizados)')}
                  </SelectItem>
                  <SelectItem key='admin' value='admin'>
                    {t('Admin (Acceso completo)')}
                  </SelectItem>
                </Select>

                {/* Mostrar permisos solo si el rol seleccionado es 'basic' */}
                {selectedUser.roles &&
                  selectedUser.roles[0] === 'basic' &&
                  selectedUser.permissions && (
                    <PermissionsSection
                      permissions={selectedUser.permissions}
                      className='mt-4'
                    />
                  )}
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={onEditClose}>
                  {t('Cancelar')}
                </Button>
                <Button
                  color='primary'
                  onPress={handleUpdateUser}
                  isLoading={loadingAction}
                >
                  {t('Actualizar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size='sm'>
        <ModalContent>
          {selectedUser && (
            <>
              <ModalHeader className='flex flex-col gap-1'>
                {t('Confirmar eliminación')}
              </ModalHeader>
              <ModalBody>
                <p>
                  {t('¿Estás seguro que deseas eliminar al usuario')}{' '}
                  <strong>
                    {selectedUser.first_name} {selectedUser.last_name}
                  </strong>
                  ?
                </p>
                <p className='text-small text-danger'>
                  {t('Esta acción no se puede deshacer.')}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={onDeleteClose}>
                  {t('Cancelar')}
                </Button>
                <Button
                  color='danger'
                  onPress={handleDeleteUser}
                  isLoading={loadingAction}
                >
                  {t('Eliminar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default TeamPage;
